import { useState, useCallback, useContext, createContext, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export type UserRole = 'investigador' | 'ugpp';

export interface AuthUser {
  id: string;
  email: string;
  nombre: string;
  role: UserRole;
  createdAt: string;
}

export type AuthError =
  | 'email_exists'
  | 'invalid_credentials'
  | 'invalid_ugpp_code'
  | 'user_not_found'
  | 'role_mismatch'
  | 'supabase_error';

export interface AuthContextValue {
  currentUser: AuthUser | null;
  isLoading: boolean;
  register: (email: string, password: string, nombre: string, role: UserRole, ugppCode?: string) => Promise<{ success: boolean; error?: AuthError }>;
  login: (email: string, password: string, role: UserRole, ugppCode?: string) => Promise<{ success: boolean; error?: AuthError }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: AuthError }>;
  emailExists: (email: string) => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchUserProfile(userId: string): Promise<AuthUser | null> {
  const { data, error } = await supabase
    .from('usuarios')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  if (error || !data) return null;
  return {
    id: data.id,
    email: data.email,
    nombre: data.nombre,
    role: data.role as UserRole,
    createdAt: data.created_at,
  };
}

/** Busca el perfil en la tabla usuarios; si no existe lo crea con datos de auth. */
async function ensureUserProfile(userId: string): Promise<AuthUser | null> {
  // 1. Intentar leer perfil existente
  const existing = await fetchUserProfile(userId);
  if (existing) return existing;

  // 2. No existe: obtener datos de auth.user para crearlo
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const email = user.email ?? '';
  const nombre = (user.user_metadata?.nombre as string) || email.split('@')[0];
  // Si viene de auth con role en metadata, úsalo; por defecto investigador
  const role: UserRole = (user.user_metadata?.role as UserRole) || 'investigador';

  const { error: insertError } = await supabase.from('usuarios').insert({
    id: userId,
    email: email.toLowerCase().trim(),
    nombre,
    role,
  });

  if (insertError) {
    console.error('Error insertando perfil de usuario:', insertError);
    return null;
  }

  return fetchUserProfile(userId);
}

export function useAuthState(): AuthContextValue {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      if (data.session?.user) {
        ensureUserProfile(data.session.user.id).then(profile => {
          if (mounted) setCurrentUser(profile);
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      if (session?.user) {
        ensureUserProfile(session.user.id).then(profile => {
          if (mounted) setCurrentUser(profile);
        });
      } else {
        setCurrentUser(null);
      }
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const register = useCallback(async (
    email: string,
    password: string,
    nombre: string,
    role: UserRole,
    ugppCode?: string,
  ): Promise<{ success: boolean; error?: AuthError }> => {
    const { data: existing } = await supabase
      .from('usuarios')
      .select('id')
      .eq('email', email.toLowerCase().trim())
      .eq('role', role)
      .maybeSingle();

    if (existing) return { success: false, error: 'email_exists' };

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: email.toLowerCase().trim(),
      password,
      options: { 
        data: { nombre, role },
        emailRedirectTo: window.location.origin 
      },
    });

    if (signUpError || !signUpData.user) {
      if (signUpError?.message?.toLowerCase().includes('already registered')) {
        return { success: false, error: 'email_exists' };
      }
      return { success: false, error: 'supabase_error' };
    }

    const { error: insertError } = await supabase.from('usuarios').insert({
      id: signUpData.user.id,
      email: email.toLowerCase().trim(),
      nombre,
      role,
    });

    if (insertError) {
      // Rollback: eliminar usuario de auth
      await supabase.auth.signOut();
      return { success: false, error: 'supabase_error' };
    }

    // Para UGPP, validar código institucional contra contraseña
    // En el flujo actual el código UGPP debe coincidir con la contraseña
    if (role === 'ugpp' && ugppCode !== password) {
      await supabase.auth.signOut();
      await supabase.from('usuarios').delete().eq('id', signUpData.user.id);
      return { success: false, error: 'invalid_ugpp_code' };
    }

    const profile = await fetchUserProfile(signUpData.user.id);
    setCurrentUser(profile);
    return { success: true };
  }, []);

  const login = useCallback(async (
    email: string,
    password: string,
    role: UserRole,
    ugppCode?: string,
  ): Promise<{ success: boolean; error?: AuthError }> => {
    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
      email: email.toLowerCase().trim(),
      password,
    });

    if (signInError || !signInData.user) {
      return { success: false, error: 'invalid_credentials' };
    }

    // Obtener o crear automáticamente el perfil si falta en tabla usuarios
    const profile = await ensureUserProfile(signInData.user.id);
    if (!profile) {
      await supabase.auth.signOut();
      return { success: false, error: 'user_not_found' };
    }

    if ( role === 'ugpp' && profile.role !== 'ugpp') {
      await supabase.auth.signOut();
      return { success: false, error: 'role_mismatch' };
    }

    if (role === 'ugpp' && ugppCode !== password) {
      await supabase.auth.signOut();
      return { success: false, error: 'invalid_ugpp_code' };
    }

    setCurrentUser(profile);
    return { success: true };
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setCurrentUser(null);
  }, []);

  const emailExists = useCallback(async (email: string): Promise<boolean> => {
    const { data } = await supabase
      .from('usuarios')
      .select('id')
      .eq('email', email.toLowerCase().trim())
      .eq('role', 'investigador')
      .maybeSingle();
    return !!data;
  }, []);

  const resetPassword = useCallback(async (email: string): Promise<{ success: boolean; error?: AuthError }> => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.toLowerCase().trim(), {
      redirectTo: window.location.origin,
    });
    if (error) return { success: false, error: 'supabase_error' };
    return { success: true };
  }, []);

  return { currentUser, isLoading, register, login, logout, resetPassword, emailExists };
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}